import { useState, useEffect } from "react";
import { X, Users, Target, DollarSign, Calendar, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Bem-vindo ao Methos!",
    description: "Este é o seu painel de controle. Aqui você acompanha seus clientes, vendas, comissões e agenda em um só lugar.",
    icon: Sparkles,
    highlight: null,
    color: "from-[#3B82F6] to-[#1d4ed8]",
  },
  {
    id: "leads",
    title: "Cadastre seus Leads",
    description: "Clique em 'Novo Cliente' para cadastrar um novo lead. Preencha nome, produto (consórcio, seguro) e valor. O lead entra automaticamente no seu funil de vendas.",
    icon: Users,
    highlight: "new-client-button",
    color: "from-[#F26A4B] to-[#e05535]",
  },
  {
    id: "funnel",
    title: "Acompanhe seu Funil",
    description: "Seu funil mostra cada lead em seu estágio atual: Qualificação, Simulação, Proposta e Fechamento. Acompanhe a evolução e mova leads entre estágios.",
    icon: Target,
    highlight: null,
    color: "from-amber-500 to-amber-600",
  },
  {
    id: "commissions",
    title: "Suas Comissões",
    description: "Acompanhe todas as suas comissões em tempo real. Veja comissões pendentes, pagas e o valor total acumulado no mês.",
    icon: DollarSign,
    highlight: null,
    color: "from-emerald-500 to-emerald-600",
  },
  {
    id: "schedule",
    title: "Organize sua Agenda",
    description: "Agende reuniões, ligações e visitas presenciais. Nunca perca um compromisso com seus clientes. Boa sorte com suas vendas!",
    icon: Calendar,
    highlight: null,
    color: "from-purple-500 to-purple-600",
  },
];

export default function OnboardingTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;
  const StepIcon = step.icon;

  useEffect(() => {
    if (step.highlight) {
      const el = document.querySelector(`[data-testid="${step.highlight}"]`);
      if (el) {
        el.classList.add("ring-4", "ring-[#3B82F6]", "ring-offset-2", "z-50", "relative");
        return () => el.classList.remove("ring-4", "ring-[#3B82F6]", "ring-offset-2", "z-50", "relative");
      }
    }
  }, [step.highlight]);

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) setCurrentStep((s) => s - 1);
  };

  const handleComplete = () => {
    setIsVisible(false);
    onComplete();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Card */}
      <div className="relative w-[90%] max-w-md mx-auto animate-in fade-in zoom-in duration-300">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header gradient */}
          <div className={`bg-gradient-to-r ${step.color} p-6 pb-10 relative`}>
            <button
              onClick={handleComplete}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
              data-testid="onboarding-skip-button"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
              <StepIcon className="w-7 h-7 text-white" />
            </div>

            <h2 className="text-xl font-bold text-white">{step.title}</h2>
          </div>

          {/* Body */}
          <div className="p-6 -mt-4">
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 leading-relaxed">{step.description}</p>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentStep
                      ? "w-6 bg-[#3B82F6]"
                      : idx < currentStep
                      ? "w-1.5 bg-[#3B82F6]/40"
                      : "w-1.5 bg-gray-200"
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrev}
                disabled={isFirst}
                className={`flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isFirst
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="onboarding-prev-button"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </button>

              <span className="text-xs text-gray-400">{currentStep + 1} de {STEPS.length}</span>

              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-5 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] transition-colors shadow-lg shadow-blue-500/20"
                data-testid="onboarding-next-button"
              >
                {isLast ? "Começar!" : "Próximo"}
                {!isLast && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Skip link */}
          <div className="px-6 pb-4 text-center">
            <button
              onClick={handleComplete}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="onboarding-skip-link"
            >
              Pular tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
